import { useCallback, useEffect, useState } from 'react';
import { useDefaultDueDate } from '../../hooks/useInvoices';

/**
 * The Due Date field of the invoice dialogs (#243): prefilled with the SCHOOL's default
 * (school today + Default Due Days, from the server), never a browser-computed 14 days.
 *
 * - A date the admin has typed is never overwritten when the default arrives or refreshes.
 * - Loading and error are distinct states with their own helper text, never a silent date.
 */
export function useDefaultDueDateField(open: boolean) {
  const { data, isLoading, isFetching, isError } = useDefaultDueDate(open);
  const [dueDate, setDueDate] = useState('');
  const [touched, setTouched] = useState(false);
  const [resetTick, setResetTick] = useState(0);

  // ⚠️ The dialogs stay MOUNTED between opens, so the cached answer can be from an earlier
  // day. Prefill only from a settled fetch (never while one is in flight), and clear an
  // untouched field on error rather than leave a day-old date in it.
  useEffect(() => {
    if (!open || touched) return;
    if (isError) setDueDate('');
    else if (!isFetching && data?.defaultDueDate) setDueDate(data.defaultDueDate);
  }, [open, touched, isError, isFetching, data?.defaultDueDate, resetTick]);

  /** The admin typed or cleared the date: theirs from now on. */
  const changeDueDate = useCallback((value: string) => {
    setTouched(true);
    setDueDate(value);
  }, []);

  /** Back to the school's default (dialog closed or reset); re-runs the prefill. */
  const resetDueDate = useCallback(() => {
    setTouched(false);
    setDueDate('');
    setResetTick((n) => n + 1);
  }, []);

  let helperText: string;
  if (isError) helperText = "Couldn't load your default due date. Choose a date.";
  else if (isLoading || isFetching || !data) helperText = 'Loading your default due date…';
  else
    helperText = `Your default: ${data.defaultDueDays} ${data.defaultDueDays === 1 ? 'day' : 'days'} from today (Payment Settings)`;

  return { dueDate, changeDueDate, resetDueDate, helperText, isError };
}
